import { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { sendFeedback, type FeedbackReason, type FeedbackVote } from "@/api/assistant";

/**
 * AC-AI-FB — 👍/👎 + (при 👎) reason-чіпи + опційний коментар. Коментар
 * проходить ТОЙ САМИЙ PII-скрабер server-side (проксі). Рендериться ЛИШЕ після
 * відповіді/no-coverage (контролює Thread). Доставка graceful — навіть якщо
 * service-endpoint ще не існує, показуємо «дякуємо» (delivered=false мовчки).
 */

const REASONS: { key: FeedbackReason; label: string }[] = [
  { key: "incorrect", label: "неправильно" },
  { key: "outdated", label: "застаріло" },
  { key: "no_source", label: "без джерела" },
  { key: "unclear", label: "незрозуміло" },
];

export function AssistantFeedback({ requestId }: { requestId: string }) {
  const [vote, setVote] = useState<FeedbackVote | null>(null);
  const [reasons, setReasons] = useState<Set<FeedbackReason>>(new Set());
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);

  if (done) {
    return <div className="mt-2.5 text-[11.5px] text-muted-foreground">Дякуємо за відгук.</div>;
  }

  const submit = (v: FeedbackVote) => {
    void sendFeedback({
      requestId,
      vote: v,
      reasons: Array.from(reasons),
      comment: comment.trim() || undefined,
    }).catch(() => undefined); // graceful — UI вже подякувало
    setDone(true);
  };

  const toggleReason = (r: FeedbackReason) =>
    setReasons((prev) => {
      const next = new Set(prev);
      next.has(r) ? next.delete(r) : next.add(r);
      return next;
    });

  return (
    <div className="mt-2.5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Відповідь допомогла?</span>
        <button
          type="button"
          aria-pressed={vote === "up"}
          onClick={() => {
            setVote("up");
            submit("up");
          }}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
            vote === "up" ? "border-primary/30 bg-primary-soft" : "border-border bg-background hover:bg-accent"
          }`}
        >
          <ThumbsUp className="h-3.5 w-3.5" aria-hidden /> Так
        </button>
        <button
          type="button"
          aria-pressed={vote === "down"}
          onClick={() => setVote("down")}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
            vote === "down" ? "border-warning-foreground/30 bg-warning" : "border-border bg-background hover:bg-accent"
          }`}
        >
          <ThumbsDown className="h-3.5 w-3.5" aria-hidden /> Ні
        </button>
      </div>

      {vote === "down" && (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {REASONS.map((r) => (
              <button
                key={r.key}
                type="button"
                aria-pressed={reasons.has(r.key)}
                onClick={() => toggleReason(r.key)}
                className={`rounded-full border px-2.5 py-1 text-[11px] ${
                  reasons.has(r.key)
                    ? "border-primary/30 bg-primary-soft text-primary-dark"
                    : "border-border bg-background text-foreground hover:bg-accent"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            aria-label="Коментар до відгуку (необов'язково)"
            placeholder="Коментар (необов'язково). Не вводьте дані клієнтів — чутливе буде замасковано."
            className="w-full resize-none rounded-md border border-border bg-background px-2.5 py-2 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <button
            type="button"
            onClick={() => submit("down")}
            className="inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Надіслати відгук
          </button>
        </div>
      )}
    </div>
  );
}
